import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Loader2, Plus, Trash2, Edit, Search, Check, Tag, Package, 
  Filter, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, 
  SlidersHorizontal, X, ArrowUpDown, Info, Download, Trash
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useSubscriptionWizard, SelectedPlan } from './SubscriptionWizardContext';
import SubscriptionWizardLayout from './SubscriptionWizardLayout';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { getHubspotLineItemsByDealId } from '@/services/business-opportunities';
import { HubspotLineItemWithOpportunity } from '@/types/hubspot';
import { addMonths, addYears, format, isValid, parseISO, subDays } from 'date-fns'; // Import date-fns functions

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  is_active: boolean;
  billing_frequency?: string;
  family_id?: string;
  sku?: string;
  description?: string;
  billing_period_type?: string;
  billing_period_duration?: number;
  subscription_period_type?: string;
  subscription_plan_prices?: SubscriptionPlanPrice[];
}

interface PlanFamily {
  id: string;
  name: string;
  description?: string;
}

const BILLING_PERIOD_UNITS = [
  { value: 'month', label: 'Mês' },
  { value: 'year', label: 'Ano' },
];

// Add a function to translate billing frequency to Portuguese
const translateBillingFrequency = (frequency?: string): string => {
  if (!frequency) return 'Não definida';
  switch (frequency.toLowerCase()) {
    case 'month':
    case 'monthly':
      return 'Mensal';
    case 'year':
    case 'yearly':
    case 'annually':
      return 'Anual';
    case 'quarterly':
      return 'Trimestral';
    case 'semiannually':
      return 'Semestral';
    case 'one-time':
    case 'unique':
      return 'Pagamento Único';
    default:
      return frequency;
  }
};

// Tipos de ordenação
type SortField = 'name' | 'price' | 'family';
type SortDirection = 'asc' | 'desc';

// Filtros avançados
interface AdvancedFilters {
  billingFrequencies: string[];
  familyIds: string[];
}

// Helper function to determine billing cycle rules
const getBillingCycleRules = (plan: SubscriptionPlan | null) => {
  if (!plan) return { defaultCycles: 1, isDisabled: false };

  const billingPeriodType = plan.billing_period_type;
  const subscriptionPeriodType = plan.subscription_period_type;
  let defaultCycles = 1;
  let isDisabled = false;

  if (billingPeriodType === 'one-time' && subscriptionPeriodType === 'one-time') {
    defaultCycles = 1;
    isDisabled = true;
  } else if (billingPeriodType === 'month' && subscriptionPeriodType === 'year') {
    defaultCycles = 12; // Always default to 12 when selecting, user can change if needed
    isDisabled = false; // Allow editing, but enforce multiple of 12 later
  } else if (billingPeriodType === 'year' && subscriptionPeriodType === 'year') {
    defaultCycles = 1;
    isDisabled = false;
  } else if (plan.billing_frequency?.toLowerCase() === 'one-time') {
    defaultCycles = 1;
    isDisabled = true; // Also disable if frequency is one-time
  } else {
    // Default cases (monthly, yearly, quarterly, semiannually added manually)
    defaultCycles = 1;
    isDisabled = false;
  }
  
  console.log(`getBillingCycleRules: Plan: ${plan.name}, Billing: ${billingPeriodType}, Subscription: ${subscriptionPeriodType}, Frequency: ${plan.billing_frequency} -> Default Cycles: ${defaultCycles}, Disabled: ${isDisabled}`);
  return { defaultCycles, isDisabled };
};

// Helper to resolve effective billing frequency from frequency or period type
const getEffectiveFrequency = (plan?: SubscriptionPlan | null): string | undefined => {
  const freq = plan?.billing_frequency?.toLowerCase();
  if (freq) return freq;
  const period = plan?.billing_period_type?.toLowerCase();
  if (period === 'month') return 'monthly';
  if (period === 'year') return 'yearly';
  if (period === 'one-time') return 'one-time';
  if (period === 'quarterly') return 'quarterly';
  if (period === 'semiannually') return 'semiannually';
  return undefined;
};

const ITEMS_PER_PAGE = 20;

// Função utilitária para logs controlados (apenas em desenvolvimento)
const debugLog = (message: string, ...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(message, ...args);
  }
};

const Step5Plans = () => {
  const navigate = useNavigate();
  const { state, addSelectedPlan, updateSelectedPlan, removeSelectedPlan } = useSubscriptionWizard();
  
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddPlanDialogOpen, setIsAddPlanDialogOpen] = useState(false);
  
  // New plan form state
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('0');
  const [billingCycles, setBillingCycles] = useState<number | undefined>(1);
  const [currentTermStart, setCurrentTermStart] = useState(state.startDate);
  const [currentTermEnd, setCurrentTermEnd] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [isEditPlanDialogOpen, setIsEditPlanDialogOpen] = useState(false);
  const [editingPlanIndex, setEditingPlanIndex] = useState<number | null>(null);
  const [isBillingCyclesDisabled, setIsBillingCyclesDisabled] = useState(false); // Add state for disabling cycle input
  
  // Mais estados para a forma de cobrança
  const [billingBy, setBillingBy] = useState<string>('by-self');
  
  // Paginação e ordenação
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Pesquisa e filtros
  const [planSearchTerm, setPlanSearchTerm] = useState('');
  const [planFamilies, setPlanFamilies] = useState<PlanFamily[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    billingFrequencies: [],
    familyIds: []
  });
  
  // Carregamento parcial
  const [totalPlans, setTotalPlans] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Opções para data de início da cobrança
  const [billingStartTerms, setBillingStartTerms] = useState<'immediately' | 'custom date'>('immediately');
  
  // Adicionar estado para renovação automática
  const [autoRenew, setAutoRenew] = useState(true);

  const [isAddingPlan, setIsAddingPlan] = useState(false);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);

  // HubSpot line items state
  const [hubspotLineItems, setHubspotLineItems] = useState<HubspotLineItemWithOpportunity[]>([]);
  const [isLoadingHubspotItems, setIsLoadingHubspotItems] = useState(false);
  const [hubspotImportAttempted, setHubspotImportAttempted] = useState(false); // Flag for import attempt
  
  // Additional state variables for auto-review mode
  const [autoReviewMode, setAutoReviewMode] = useState(false);
  const [currentReviewIndex, setCurrentReviewIndex] = useState<number | null>(null);
  

  
  // Function to fetch a page of plans - MODIFIED to return fetched plans
  const fetchPlansPage = async (page: number): Promise<SubscriptionPlan[] | null> => {
    setLoadingMore(true);
    const ITEMS_PER_PAGE = 20; // Or get from constant
    const offset = (page - 1) * ITEMS_PER_PAGE;

    try {
      let query = supabase
        .from('subscription_plans')
        // Use the correct relationship name 'subscription_plan_prices'
        .select('*, subscription_plan_families(id, name), subscription_plan_prices(*)', { count: 'exact' })
        .eq('is_active', true)
        // Add filters based on advancedFilters
        .order(sortField === 'family' ? 'subscription_plan_families(name)' : sortField, { ascending: sortDirection === 'asc' })
        .range(offset, offset + ITEMS_PER_PAGE - 1);

      // Apply currency filter using the correct relationship name
      query = query.eq('subscription_plan_prices.currency', state.currencyCode);

      // Apply advanced filters
      if (advancedFilters.billingFrequencies.length > 0) {
        query = query.in('billing_frequency', advancedFilters.billingFrequencies);
      }
      if (advancedFilters.familyIds.length > 0) {
        query = query.in('family_id', advancedFilters.familyIds);
      }
      if (planSearchTerm) {
        query = query.ilike('name', `%${planSearchTerm}%`);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      const fetchedPlans = data as SubscriptionPlan[];
      // Filter plans locally to ensure they have a price for the selected currency 
      // Use the correct nested array name 'subscription_plan_prices'
      const validPlans = fetchedPlans.filter(p => 
          p.subscription_plan_prices && 
          p.subscription_plan_prices.some(price => price.currency === state.currencyCode)
      );

      // Add/Update the top-level 'price' field for compatibility
      const plansWithPrice = validPlans.map(plan => ({
          ...plan,
          // Find the correct price within the nested array
          price: plan.subscription_plan_prices?.find(price => price.currency === state.currencyCode)?.price_amount || 0,
          // Keep the original nested structure as well, renaming it to 'prices' for potential internal use if needed?
          // Or just rely on subscription_plan_prices directly downstream? Let's keep original name.
          // prices: plan.subscription_plan_prices 
      }));

      setPlans(page === 1 ? plansWithPrice : [...plans, ...plansWithPrice]);
      setTotalPlans(count || 0); // Update total count based on query
      setCurrentPage(page);
      console.log(`fetchPlansPage(${page}) successful, returning ${plansWithPrice.length} valid plans.`);
      return plansWithPrice; // RETURN fetched, validated, and formatted plans

    } catch (error) {
      console.error(`Error fetching plans page ${page}:`, error);
      toast.error(`Erro ao carregar página ${page} de planos.`);
      return null; // <<< RETURN null on error
    } finally {
      setLoadingMore(false);
    }
  };

  // Ref para controlar chamadas duplicadas
  const loadingInProgress = useRef(false);
  const lastLoadKey = useRef<string>('');
  
  // Cache para detalhes de planos
  const planDetailsCache = useRef<Map<string, SubscriptionPlan>>(new Map());

  // Main effect for initial data loading using async/await
  useEffect(() => {
    if (!state.selectedCustomer || !state.currencyCode) {
      navigate('../step1', { relative: 'path' });
      return;
    }

    // Criar chave única para esta execução
    const currentLoadKey = `${state.selectedCustomer?.id}_${state.currencyCode}_${state.selectedOpportunity?.id}`;
    
    // Evitar chamadas duplicadas
    if (loadingInProgress.current || lastLoadKey.current === currentLoadKey) {
      console.log("LoadData já em execução ou já processado, ignorando chamada duplicada");
      return;
    }
    
    const loadData = async () => {
      loadingInProgress.current = true;
      lastLoadKey.current = currentLoadKey;
    setLoading(true);
        setHubspotLineItems([]); // Reset HubSpot items
        // Reset plans state as well, it will be populated later for UI
        // setPlans([]); 
        console.log("Step5Plans Initialization (async): Fetching families/count...");

        try {
            // Step 1: Fetch families/count (Remove initial plan fetch)
            await Promise.all([
                fetchPlanFamilies(),
                fetchPlansCount(),
                // fetchPlansPage(1) // <-- REMOVE initial plan fetch from here
            ]);
            console.log("Local families/count fetched.");

            let relevantLocalPlans: SubscriptionPlan[] = []; // Store plans needed for conversion

            // Step 2: Fetch HubSpot items if opportunity exists
        if (state.selectedOpportunity?.id) {
                console.log("Fetching HubSpot items for opportunity:", state.selectedOpportunity.id);
                setIsLoadingHubspotItems(true);
                let lineItems: HubspotLineItemWithOpportunity[] = [];
                try {
                    lineItems = await getHubspotLineItemsByDealId(state.selectedOpportunity.id);
                            debugLog("HubSpot items fetched successfully:", lineItems);
        setHubspotLineItems(lineItems); // Store raw items

        // Step 2b: If items found, extract IDs and fetch relevant local plans
        if (lineItems.length > 0) {
          const entityIds = [...new Set(lineItems.map(item => item.entity_id).filter(Boolean) as string[])];
          debugLog(`Found ${entityIds.length} unique entity_ids in HubSpot items. Fetching corresponding local plans...`);

                        if (entityIds.length > 0) {
                            const { data: planData, error: planError } = await supabase
                                .from('subscription_plans')
                                .select('*, subscription_plan_families(id, name), subscription_plan_prices(*)')
                                .in('id', entityIds)
                                .eq('is_active', true)
                                // Also ensure we get prices for the correct currency for these specific plans
                                .eq('subscription_plan_prices.currency', state.currencyCode);

                            if (planError) {
                                console.error("Error fetching relevant local plans by ID:", planError);
                                toast.error("Erro ao buscar planos locais correspondentes aos itens HubSpot.");
      } else {
                                const fetchedPlans = planData as SubscriptionPlan[];
                                // Filter fetched plans for valid price entry (redundant due to .eq filter, but safe)
                                const validPlans = fetchedPlans.filter(p => 
                                    p.subscription_plan_prices && 
                                    p.subscription_plan_prices.some(price => price.currency === state.currencyCode)
                                );
                                // Add top-level price field for consistency
                                relevantLocalPlans = validPlans.map(plan => ({
                                    ...plan,
                                    price: plan.subscription_plan_prices?.find(price => price.currency === state.currencyCode)?.price_amount || 0,
                                }));
                                debugLog(`Successfully fetched ${relevantLocalPlans.length} relevant local plans.`);
                            }
                        }
                    }

                    // Step 3: Conversion - Use the specifically fetched relevantLocalPlans
                    console.log("Checking conversion conditions: Relevant Local Plans:", relevantLocalPlans.length, "HubSpot Items:", lineItems.length, "Import Attempted:", hubspotImportAttempted);

                    // Condition to run conversion: relevant plans found, items found, AND import not yet attempted for this opportunity
                    if (relevantLocalPlans.length > 0 && lineItems.length > 0 && !hubspotImportAttempted) {
                        console.log("Conditions met. Attempting conversion using relevant local plans...");
                        setHubspotImportAttempted(true); // Set flag to true after attempting import
                        
                        const conversionResults = lineItems.map(item => {
                            const plan = convertHubspotItemToSubscriptionPlan(item, relevantLocalPlans); // Use relevant plans
                            return { item, plan };
                        });
                        // ... (rest of processing conversionResults, adding plans, showing toasts) ...
                        const plansToAdd = conversionResults
                            .filter(result => result.plan !== null)
                            .map(result => result.plan) as SelectedPlan[];
                        
                        // Filter out duplicate plans before adding them
                        const uniquePlansToAdd = plansToAdd.filter(planToAdd => {
                            // Check for duplicates based on subscription_plan_id
                            const isDuplicate = state.selectedPlans.some(existingPlan => 
                                existingPlan.subscription_plan_id === planToAdd.subscription_plan_id
                            );
                            
                            // Log for debugging
                            if (isDuplicate) {
                                console.log("Found duplicate plan:", planToAdd.subscription_plan.name, "ID:", planToAdd.subscription_plan_id);
                            }
                            
                            return !isDuplicate;
                        });
                            
                        const failedItems = conversionResults
                            .filter(result => result.plan === null)
                            .map(result => result.item.name || `(ID: ${result.item.entity_id || 'N/A'})`); // Show ID on failure

                        // Count plans that were filtered out as duplicates
                        const duplicateCount = plansToAdd.length - uniquePlansToAdd.length;

                        if (uniquePlansToAdd.length > 0) {
                            console.log(`Adding ${uniquePlansToAdd.length} converted plans (${duplicateCount} duplicates filtered out).`);
                            uniquePlansToAdd.forEach(plan => addSelectedPlan(plan));
                            toast.info(`${uniquePlansToAdd.length} plano(s) importado(s) da oportunidade. Por favor, revise os detalhes.`);
                            
                            if (duplicateCount > 0) {
                                toast.warning(`${duplicateCount} plano(s) já existente(s) na assinatura foram ignorados para evitar duplicação.`);
                            }
                        } else if (plansToAdd.length > 0 && uniquePlansToAdd.length === 0) {
                            // All plans were duplicates
                            toast.warning(`Todos os ${plansToAdd.length} plano(s) da oportunidade já estão adicionados à assinatura. Nenhum novo plano foi adicionado para evitar duplicação.`);
                        }

                        if (failedItems.length > 0) {
                            console.warn(`Could not map the following ${failedItems.length} HubSpot items (check entity_id exists and is active):`, failedItems.join(", "));
                            toast.warning(`Itens não mapeados (ID): ${failedItems.join(", ")}. Verifique se o plano existe, está ativo e possui preço para ${state.currencyCode}.`);
                        }

      } else {
                        console.log("Skipping conversion: Conditions not met (No relevant local plans found?, No HubSpot items?, Plans already added?).");
                    }
                } catch (hubspotError) {
                    console.error("Error processing HubSpot items or fetching relevant plans:", hubspotError);
                    toast.error("Erro ao processar itens da oportunidade HubSpot.");
                } finally {
                    setIsLoadingHubspotItems(false);
                }
            } else {
                console.log("No HubSpot opportunity ID found, skipping HubSpot processing.");
            }
            
            // Step 4: Fetch initial plans for UI display (Add Plan dialog)
            // This runs after conversion attempt, ensuring conversion uses specific data
            console.log("Fetching initial plans page for UI display...");
            await fetchPlansPage(1); // Populate the 'plans' state for the dialog

        } catch (initialError) {
            // Catches errors from the initial families/count Promise.all
            console.error("Error during initial data fetch (families/count):", initialError);
            toast.error("Erro ao carregar dados iniciais (famílias/contagem).");
        } finally {
            setLoading(false);
            loadingInProgress.current = false;
            console.log("Initial loading async function finished.");
        }
    };

    loadData(); // Execute the async function

  }, [state.selectedCustomer?.id, state.currencyCode, state.selectedOpportunity?.id]); // Dependencies

  // Reset import attempted flag when opportunity changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setHubspotImportAttempted(false);
      console.log("Opportunity changed, resetting hubspotImportAttempted flag.");
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [state.selectedOpportunity?.id]);

  // Effect to set initial billing start terms based on start date
  useEffect(() => {
    if (currentTermStart) {
      const selectedDate = new Date(currentTermStart);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      
      if (selectedDate.getTime() === today.getTime()) {
        setBillingStartTerms('immediately');
      } else {
        setBillingStartTerms('specific-date');
      }
    }
  }, [currentTermStart]);

  // REMOVE the useEffect hook that depended on localPlansReady
  /*
  useEffect(() => {
     // ... previous conversion logic based on localPlansReady ...
  }, [localPlansReady, hubspotLineItems, plans, state.selectedPlans.length, addSelectedPlan]); 
  */

  // Conta o total de planos para paginação
  const fetchPlansCount = async () => {
    try {
      const query = supabase
        .from('subscription_plans')
        .select('id', { count: 'exact' })
        .eq('is_active', true);
      
      // Aplicar filtros avançados na contagem
      if (advancedFilters.billingFrequencies.length > 0) {
        query.in('billing_frequency', advancedFilters.billingFrequencies);
      }
      
      if (advancedFilters.familyIds.length > 0) {
        query.in('family_id', advancedFilters.familyIds);
      }
      
      const { count, error } = await query;
      
      if (error) throw error;
      
      setTotalPlans(count || 0);
    } catch (error) {
      console.error('Error counting plans:', error);
    }
  };

  const fetchPlanFamilies = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .select('id, name, description');
      
      if (error) throw error;
      
      setPlanFamilies(data || []);
    } catch (error) {
      console.error('Error fetching plan families:', error);
    }
  };

  // Função para alternar a direção da ordenação
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Função para atualizar filtros avançados
  const updateAdvancedFilters = (partialFilters: Partial<AdvancedFilters>) => {
    setAdvancedFilters(prev => ({
      ...prev,
      ...partialFilters
    }));
    setCurrentPage(1); // Voltar para a primeira página ao filtrar
  };

  // Função para limpar todos os filtros
  const clearAllFilters = () => {
    setAdvancedFilters({
      billingFrequencies: [],
      familyIds: []
    });
    setPlanSearchTerm('');
    setCurrentPage(1);
  };

  // Função para verificar se há filtros ativos
  const hasActiveFilters = useMemo(() => {
    return (
      planSearchTerm !== '' ||
      advancedFilters.billingFrequencies.length > 0 ||
      advancedFilters.familyIds.length > 0
    );
  }, [planSearchTerm, advancedFilters]);

  // Calcular o número total de páginas
  const totalPages = Math.ceil(totalPlans / ITEMS_PER_PAGE);

  // Função para navegar para uma página específica
  const goToPage = (page: number) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  // Modificar o handlePlanChange para configurar billing_cycles de acordo com as regras de negócio
  const handlePlanChange = (planId: string) => {
    setSelectedPlanId(planId);
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;
    
    setSelectedPlan(plan);
    
    // Get price for selected currency
    if (plan.subscription_plan_prices && plan.subscription_plan_prices.length > 0) {
      const priceObj = plan.subscription_plan_prices.find(p => p.currency === state.currencyCode);
      if (priceObj) {
        setUnitPrice(priceObj.price_amount.toString());
      } else {
        // Default to first price if no matching currency found
        setUnitPrice(plan.subscription_plan_prices[0]?.price_amount.toString() || '0');
      }
    } else {
      setUnitPrice('0');
    }
    
    // Set billing cycles based on plan type and business rules
    const { defaultCycles } = getBillingCycleRules(plan);
    setBillingCycles(defaultCycles);
    
    // Recalcular a data de término com base no novo plano e ciclos padrão
    if (currentTermStart && (plan.billing_frequency || plan.billing_period_type)) {
      const endDate = calculateEndDate(
        currentTermStart,
        defaultCycles,
        getEffectiveFrequency(plan)
      );
      setCurrentTermEnd(endDate);
    }
  };

  // Modificar o handleAddPlan para validar as regras antes de adicionar o plano
  const handleAddPlan = async () => {
    setIsAddingPlan(true);
    try {
      if (!selectedPlan) {
      toast.error('Selecione um plano');
      return;
    }

    if (!quantity || parseInt(quantity, 10) <= 0) {
      toast.error('A quantidade deve ser maior que zero');
      return;
    }

    if (!unitPrice || parseFloat(unitPrice) < 0) {
        toast.error('O preço unitário não pode ser negativo');
      return;
    }

      // Validar regras de negócio para billing_cycles
      const billingPeriodType = selectedPlan.billing_period_type;
      const subscriptionPeriodType = selectedPlan.subscription_period_type;
      
      if (billingPeriodType === 'month' && subscriptionPeriodType === 'year') {
        // Validar que billing_cycles é múltiplo de 12 e mínimo 12
        if (!billingCycles || billingCycles < 12) {
          toast.error('O período de cobrança deve ser no mínimo 12 para este tipo de plano');
      return;
    }

        if (billingCycles % 12 !== 0) {
          toast.error('O período de cobrança deve ser múltiplo de 12 para este tipo de plano');
      return;
    }
      } else if (!billingCycles || billingCycles <= 0) {
        toast.error('O período de cobrança deve ser maior que zero');
      return;
    }

      if (!currentTermStart || !currentTermEnd) {
        toast.error('As datas de início e término do termo devem ser definidas');
      return;
    }

      // Get billing frequency from plan
      let termStart = currentTermStart;
      
      // Verifica se este plano já está na lista
      const planAlreadySelected = state.selectedPlans.some(
        existingPlan => existingPlan.subscription_plan_id === selectedPlan.id
      );
      
      // Log para debug
      if (planAlreadySelected) {
        console.log("Detected duplicate plan during manual addition:", selectedPlan.name, "ID:", selectedPlan.id);
        toast.error('Este plano já foi adicionado à assinatura. Edite o item existente para alterar a quantidade.');
        return;
      }
      
      // Cria o plano selecionado
    addSelectedPlan({
        subscription_plan_id: selectedPlan.id,
        subscription_plan: selectedPlan,
      quantity: parseInt(quantity, 10),
      unit_price: parseFloat(unitPrice),
      current_term_start: currentTermStart,
      current_term_end: currentTermEnd,
      billing_start_terms: billingStartTerms,
        auto_renew: autoRenew,
        billing_cycles: billingCycles,
        billing_by: billingBy,
        term_start: termStart
    });

      // Limpa o formulário
    setSelectedPlanId('');
    setQuantity('1');
    setUnitPrice('0');
      setBillingCycles(1);
    setCurrentTermStart(state.startDate);
    setCurrentTermEnd('');
    setBillingStartTerms('immediately');
      setSelectedPlan(null);
    setAutoRenew(true);
      setBillingBy('by-self');
    
    setIsAddPlanDialogOpen(false);
    toast.success('Plano adicionado com sucesso');
    } catch (error) {
      console.error('Error adding plan:', error);
      toast.error('Erro ao adicionar plano');
    } finally {
      setIsAddingPlan(false);
    }
  };

  const calculateTotalAmount = () => {
    return state.selectedPlans.reduce((total, selectedPlan) => {
      // Get the original plan from the plans array
      const plan = plans.find(p => p.id === selectedPlan.subscription_plan.id);
      if (!plan) {
        // Fallback to the unit_price if the plan is not found
        return total + (selectedPlan.quantity * selectedPlan.unit_price);
      }
      
      const price = getPlanPrice(plan, state.currencyCode);
      return total + (selectedPlan.quantity * price);
    }, 0);
  };




  const handleSubmit = () => {
    // Check if any plan needs review before proceeding
    const plansNeedingReview = state.selectedPlans.filter(plan => plan.needsReview);
    if (plansNeedingReview.length > 0) {
      toast.error(`Por favor, revise os ${plansNeedingReview.length} plano(s) importado(s) antes de continuar.`);
      // Automatically open the edit modal for the first plan needing review
      const firstReviewIndex = state.selectedPlans.findIndex(plan => plan.needsReview);
      if (firstReviewIndex !== -1) {
        handleEditPlan(firstReviewIndex);
      }
      return; // Prevent proceeding to the next step
    }

    if (state.selectedPlans.length === 0) {
      toast.error("Adicione pelo menos um plano para continuar.");
      return;
    }

    // Redirect based on subscription type
    if (state.subscriptionType === 'split') {
      navigate('../step5b', { relative: 'path' });
    } else {
      navigate('../step6', { relative: 'path' });
    }
  };

  // Ref para controlar chamadas duplicadas de handleEditPlan
  const editPlanInProgress = useRef(false);

  // Modificar handleEditPlan para buscar detalhes do plano diretamente se não encontrado na lista
  const handleEditPlan = async (index: number) => { // Tornar a função async
    // Prevenir chamadas duplicadas
    if (editPlanInProgress.current) {
      console.log("handleEditPlan já em execução, ignorando chamada duplicada");
      return;
    }

    editPlanInProgress.current = true;
    console.log("--- handleEditPlan triggered ---");
    const planItem = state.selectedPlans[index];
    console.log("Selected plan item from context state:", planItem);
    
    // Tenta buscar o plano original completo da lista de planos carregados
    let originalPlan = plans.find(p => p.id === planItem.subscription_plan_id);
    
    // Se não encontrou, verifica no cache de detalhes de planos
    if (!originalPlan && planDetailsCache.current.has(planItem.subscription_plan_id)) {
      originalPlan = planDetailsCache.current.get(planItem.subscription_plan_id)!;
      debugLog("Plan details retrieved from cache for ID:", planItem.subscription_plan_id);
    }
    
    // Se não encontrou, verifica se o plano está embarcado no próprio item
    if (!originalPlan && planItem.subscription_plan) {
      originalPlan = planItem.subscription_plan as SubscriptionPlan;
      debugLog("Plan details retrieved from embedded plan data for ID:", planItem.subscription_plan_id);
    }
    
    if (!originalPlan) {
      console.warn("Original plan details not found in fetched plans list for ID:", planItem.subscription_plan_id, ". Fetching directly...");
      try {
        // Buscar diretamente do Supabase pelo ID using correct relationship name
        const { data: directPlanData, error: directPlanError } = await supabase
          .from('subscription_plans')
          .select(`
            id, name, description, billing_frequency, family_id, sku, is_active,
            billing_period_type, billing_period_duration, subscription_period_type,
            subscription_plan_prices:subscription_plan_prices(id, currency, price_amount)
          `)
          .eq('id', planItem.subscription_plan_id)
          .maybeSingle();

        if (directPlanError) throw directPlanError;
        
        if (directPlanData) {
          console.log("Successfully fetched plan details directly:", directPlanData);
          // Formatar os dados como esperado pela interface SubscriptionPlan
          const prices = directPlanData.subscription_plan_prices || []; // Use correct name
          let price = 0;
          if (prices && prices.length > 0) {
            const priceObj = prices.find((p: any) => p.currency === state.currencyCode);
            price = priceObj ? priceObj.price_amount : (prices[0]?.price_amount || 0);
          }
          originalPlan = {
            ...directPlanData,
            price: price, // Add the top-level price property
            currency: state.currencyCode,
            // Keep the original nested structure
            subscription_plan_prices: prices.map((p: any) => ({ 
              id: p.id,
              // subscription_plan_id is implicit in the relationship
              currency: p.currency,
              price_amount: p.price_amount
            }))
          } as SubscriptionPlan;
          
          // Adicionar ao cache para futuras consultas
          planDetailsCache.current.set(planItem.subscription_plan_id, originalPlan);
        } else {
          console.error("CRITICAL: Plan not found even when fetching directly by ID:", planItem.subscription_plan_id);
          toast.error("Erro Crítico: Detalhes do plano não encontrados no banco de dados.");
          return;
        }
      } catch(error) {
        console.error("CRITICAL: Error fetching plan details directly:", error);
        toast.error("Erro ao buscar detalhes do plano original.");
        return;
      }
    }
    // Removendo log verboso
    // console.log("Using Original plan details:", originalPlan);
    
    setSelectedPlanId(originalPlan.id);
    setSelectedPlan(originalPlan); 
    setQuantity(planItem.quantity.toString());
    // Usar o preço unitário armazenado no item da assinatura, pois pode ter sido customizado
    setUnitPrice(planItem.unit_price.toString()); 
    
    // Get billing cycle rules using the helper function
    const { defaultCycles, isDisabled } = getBillingCycleRules(originalPlan);
    
    // Apply rules: Use existing cycles if present, otherwise use default from rules/item
    // Apply rules: Use existing cycles if present, otherwise use default from rules
    const initialBillingCycles = planItem.billing_cycles !== undefined && planItem.billing_cycles !== null 
                                 ? planItem.billing_cycles 
                                 : defaultCycles;
    
    // Adjust month/year rule if existing cycles are invalid
    if (originalPlan.billing_period_type === 'month' && originalPlan.subscription_period_type === 'year') {
      if (initialBillingCycles < 12 || initialBillingCycles % 12 !== 0) {
        console.log("Rule: month/year in edit -> adjusting cycles to 12 (was < 12 or not multiple)");
        setBillingCycles(12);
      } else {
         setBillingCycles(initialBillingCycles); 
      }
    } else {
      // For other types, ensure it's at least 1 or use the value from rules/item
      setBillingCycles(Math.max(1, initialBillingCycles));
    }
    
    setIsBillingCyclesDisabled(isDisabled); // Set the disabled state
    
    console.log("handleEditPlan -> Final initialBillingCycles:", billingCycles, "isDisabled:", isDisabled);
    
    setCurrentTermStart(planItem.current_term_start);
    // A data final será recalculada pelo useEffect quando o modal abrir
    setCurrentTermEnd(''); // Limpa temporariamente para forçar recálculo
    
    setBillingStartTerms(planItem.billing_start_terms || 'immediately');
    setAutoRenew(planItem.auto_renew !== undefined ? planItem.auto_renew : true);
    setBillingBy(planItem.billing_by || 'by-self');
    setEditingPlanIndex(index);
    
    console.log("Opening edit modal...");
    setIsEditPlanDialogOpen(true);
    editPlanInProgress.current = false; // Reset controle
  };

  const handleUpdatePlan = () => {
    if (editingPlanIndex === null || !selectedPlan) {
        console.error("Update attempted without selected plan or index.");
        toast.error("Erro interno: Plano para edição não encontrado.");
        return;
      }

    // --- Add Validation Here ---
    const billingPeriodType = selectedPlan.billing_period_type;
    const subscriptionPeriodType = selectedPlan.subscription_period_type;
    const currentCycles = billingCycles || 0; // Use current state value
      
      if (billingPeriodType === 'month' && subscriptionPeriodType === 'year') {
      if (currentCycles < 12) {
        toast.error("Para planos mensais com período anual, o mínimo são 12 ciclos de cobrança.");
      return;
    }
      if (currentCycles % 12 !== 0) {
        toast.error("Para planos mensais com período anual, os ciclos de cobrança devem ser múltiplos de 12.");
      return;
    }
    } else if (currentCycles < 1) {
        // Basic validation for other types
        toast.error("Os ciclos de cobrança devem ser pelo menos 1.");
      return;
    }
    // --- End Validation ---

    setIsUpdatingPlan(true);

    try {
        // Recalcular a data de término usando nossa função utilitária
        const termEndDate = calculateEndDate(
          currentTermStart,
          typeof billingCycles === 'number' ? billingCycles : parseInt(billingCycles as any, 10) || 1,
          selectedPlan.billing_frequency || ''
        );
        
        console.log("Data de término calculada:", {
          start: currentTermStart,
          cycles: billingCycles,
          freq: selectedPlan.billing_frequency,
          endDate: termEndDate
        });

        // Construct the update payload, ensuring needsReview is set to false
        const updatedPlanData: Partial<SelectedPlan> = {
      quantity: parseInt(quantity, 10),
      unit_price: parseFloat(unitPrice),
      current_term_start: currentTermStart,
          current_term_end: termEndDate, // Use a data recalculada
      billing_start_terms: billingStartTerms,
        auto_renew: autoRenew,
        billing_cycles: billingCycles,
        billing_by: billingBy,
          needsReview: false, // Mark as reviewed
        };

        console.log(`Updating plan at index ${editingPlanIndex} with data:`, updatedPlanData);
        updateSelectedPlan(editingPlanIndex, updatedPlanData);
        
        // If in auto-review mode, find the next plan that needs review
        if (autoReviewMode) {
            // Find the next plan that needs review
            const nextIndex = state.selectedPlans.findIndex(
                (plan, idx) => idx > editingPlanIndex && plan.needsReview
            );
            
            if (nextIndex !== -1) {
                // Show success message for current plan, then move to next plan
                toast.success(`Plano atualizado. Avançando para o próximo...`);
                
                // Close current dialog first
                setIsEditPlanDialogOpen(false);
                
                // Short timeout to allow dialog to close before opening the next one
                setTimeout(() => {
                    setEditingPlanIndex(nextIndex);
                    setCurrentReviewIndex(nextIndex);
                    handleEditPlan(nextIndex);
                }, 300);
            } else {
                // All plans reviewed
                toast.success("Todos os planos foram revisados com sucesso!");
                setAutoReviewMode(false);
                setCurrentReviewIndex(null);
    setIsEditPlanDialogOpen(false);
      setEditingPlanIndex(null);
            }
        } else {
            // Regular mode (manual editing) - just close the dialog
            toast.success("Plano atualizado com sucesso!");
            setIsEditPlanDialogOpen(false);
            setEditingPlanIndex(null);
        }
    } catch (error) {
        console.error("Error updating plan:", error);
        toast.error("Erro ao atualizar o plano.");
    } finally {
      setIsUpdatingPlan(false);
    }
  };

  const clearSearch = () => {
    setPlanSearchTerm('');
  };

  // Reset search and selected family when dialog is opened or closed
  useEffect(() => {
    // Este useEffect lida com a limpeza QUANDO o diálogo de ADICIONAR é FECHADO
    if (!isAddPlanDialogOpen) {
      setPlanSearchTerm(''); // Resetar termo de pesquisa
      setCurrentPage(1); // Voltar para a primeira página
      setSelectedPlanId(''); // Limpar ID do plano selecionado
      setSelectedPlan(null); // Limpar objeto do plano selecionado
      // Resetar estados do formulário de adição para seus valores padrão
      setQuantity('1');
      setUnitPrice('0');
      setBillingCycles(1);
      setCurrentTermStart(state.startDate); // Usar data de início global do wizard
      setCurrentTermEnd('');
      setBillingStartTerms('immediately');
      setAutoRenew(true);
      setBillingBy('by-self');
    }
  }, [isAddPlanDialogOpen, state.startDate]);

  // Atualiza a data de início da vigência com base na opção de início da cobrança
  // (Este useEffect parece correto e deve permanecer)
  useEffect(() => {
    if (billingStartTerms === 'immediately') {
      const today = new Date();
      setCurrentTermStart(today.toISOString().split('T')[0]);
    }
  }, [billingStartTerms]);

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    setSelectedPlanId(plan.id);
    handlePlanChange(plan.id);
  };

  // Adicione esta função para traduzir o billing_start_terms
  const translateBillingStartTerms = (term?: string) => {
    if (!term) return '';
    
    switch (term) {
      case 'immediately':
        return 'Imediatamente';
      case 'custom date':
        return 'Data personalizada';
      default:
        return term;
    }
  };

  // Função para formatar datas
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Função para formatar o termo de compromisso
  const formatCommitmentTerm = (billingPeriodType: string | null | undefined, billingPeriodDuration: number | null | undefined, billingFrequency: string | null | undefined) => {
    if (!billingFrequency) return '-';
    
    // Se for um plano one-time, retornar "Não se aplica"
    if (billingFrequency.toLowerCase() === 'one-time') {
      return 'Não se aplica';
    }
    
    if (!billingPeriodType || !billingPeriodDuration) return '-';
    
    if (billingPeriodType === 'month') {
      return billingPeriodDuration === 1 ? '1 mês' : `${billingPeriodDuration} meses`;
    } else if (billingPeriodType === 'year') {
      return billingPeriodDuration === 1 ? '1 ano' : `${billingPeriodDuration} anos`;
    } else if (billingPeriodType === 'one-time') {
      return 'Pagamento único';
    }
    
    return `${billingPeriodDuration} x ${translateBillingFrequency(billingFrequency)}`;
  };

  // Helper function to get the price for a plan in the specified currency
  const getPlanPrice = (plan: SubscriptionPlan, currency: string): number => {
    // Use the correct nested array name 'subscription_plan_prices'
    if (plan.subscription_plan_prices && plan.subscription_plan_prices.length > 0) {
      const priceObj = plan.subscription_plan_prices.find(p => p.currency === currency);
      if (priceObj) return priceObj.price_amount;
      // Fallback: try finding the first price if currency doesn't match (Be cautious)
      if (plan.subscription_plan_prices[0]) {
        console.warn(`Price for currency ${currency} not found for plan ${plan.id}. Falling back to first available price (${plan.subscription_plan_prices[0].currency}).`);
        return plan.subscription_plan_prices[0].price_amount; 
      }
    }
    // Final fallback if no prices array exists or is empty
    console.warn(`No prices found in subscription_plan_prices for plan ${plan.id}. Falling back to plan.price or 0.`);
    return plan.price || 0; // Keep existing fallback just in case (though plan.price might be stale)
  };

  // Modificar a função formatPriceWithFrequency para tratar planos one-time
  const formatPriceWithFrequency = (plan: SubscriptionPlan) => {
    const price = getPlanPrice(plan, state.currencyCode);
    const formattedPrice = formatCurrency(price, state.currencyCode);
    
    if (!plan.billing_frequency) return formattedPrice;
    
    const frequencyLower = plan.billing_frequency.toLowerCase();
    
    if (frequencyLower === 'one-time') {
      return `${formattedPrice}/único`;
    } else if (frequencyLower === 'monthly') {
      return `${formattedPrice}/mês`;
    } else if (frequencyLower === 'yearly') {
      return `${formattedPrice}/ano`;
    } else if (frequencyLower === 'quarterly') {
      return `${formattedPrice}/trimestre`;
    } else if (frequencyLower === 'semiannually') {
      return `${formattedPrice}/semestre`;
    } else if (frequencyLower === 'annually') {
      return `${formattedPrice}/ano`;
    }
    
    // Default case for other frequencies
    const translatedFrequency = translateBillingFrequency(frequencyLower);
    return `${formattedPrice}/${translatedFrequency}`;
  };

  // Function to convert HubSpot line item to subscription plan
  const convertHubspotItemToSubscriptionPlan = (
    hubspotItem: HubspotLineItemWithOpportunity,
    localPlans: SubscriptionPlan[] // Accept local plans as argument
  ): SelectedPlan | null => {
    console.log("Attempting to convert HubSpot line item:", hubspotItem);

    // Use the provided localPlans argument for matching
    if (!localPlans || localPlans.length === 0) {
        console.error("Local plans data not provided or empty for conversion.");
        return null;
    }

    // Find the matching subscription plan in the provided localPlans using entity_id
    const matchedPlan = localPlans.find(p => 
        hubspotItem.entity_id && p.id === hubspotItem.entity_id
    );

    if (!matchedPlan) {
      // Log the specific item that failed using entity_id
      console.error(`--> Mapping Failed: Could not find local plan with entity_id ${hubspotItem.entity_id}`);
      return null;
    }
    console.log(`Found matching local plan for entity_id ${hubspotItem.entity_id}:`, matchedPlan);

    // Use matchedPlan data to fill the SelectedPlan structure
    // Get price from HubSpot item with discount applied (price - discount), fallback to matched local plan price using the helper
    const hubspotPrice = parseFloat(String(hubspotItem.price)) || 0;
    const hubspotDiscount = parseFloat(String(hubspotItem.discount)) || 0;
    const hubspotUnitPriceWithDiscount = hubspotPrice - hubspotDiscount;
    const unitPrice = hubspotPrice > 0 ? hubspotUnitPriceWithDiscount : getPlanPrice(matchedPlan, state.currencyCode);
    
    // Debug log para verificar o cálculo do desconto
    if (hubspotPrice > 0) {
      console.log(`Cálculo valor unitário - Price: ${hubspotPrice}, Discount: ${hubspotDiscount}, Final: ${hubspotUnitPriceWithDiscount}`);
    }
    const quantity = parseInt(String(hubspotItem.quantity)) || 1;
    // Prefer HubSpot recurring billing start date when provided (YYYY-MM-DD)
    const hubspotStartRaw = (hubspotItem as any).hs_recurring_billing_start_date as string | undefined;
    let startDate = state.startDate || new Date().toISOString().split('T')[0];
    if (hubspotStartRaw) {
      const candidate = hubspotStartRaw.includes('T') ? hubspotStartRaw.split('T')[0] : hubspotStartRaw;
      const parsed = parseISO(candidate);
      if (isValid(parsed)) {
        startDate = candidate;
      }
    }
    let termEndDate = '';
    let billingCyclesValue = matchedPlan.billing_period_duration || 1; // Default billing cycles from plan or 1

    // Initial term/end date calculation logic based on matched plan
    try {
      const start = new Date(startDate);
      // Adjust start date to the first day of the month if needed?
      // start.setDate(1);
      let end = new Date(start);

      const billingFrequency = matchedPlan.billing_frequency?.toLowerCase();
      const termMonths = hubspotItem.hs_term_in_months ? parseInt(hubspotItem.hs_term_in_months, 10) : null;

      // Determine cycles based on term and frequency
      if (termMonths) {
        if (billingFrequency === 'monthly' || billingFrequency === 'month') billingCyclesValue = termMonths;
        else if (billingFrequency === 'yearly' || billingFrequency === 'annually' || billingFrequency === 'year') billingCyclesValue = termMonths / 12;
        else if (billingFrequency === 'quarterly') billingCyclesValue = termMonths / 3;
        else if (billingFrequency === 'semiannually') billingCyclesValue = termMonths / 6;
        else billingCyclesValue = termMonths; // Default to term if frequency unknown/one-time?
      } else {
        // Default cycles if no term specified in HubSpot
        if (billingFrequency === 'yearly' || billingFrequency === 'annually' || billingFrequency === 'year') billingCyclesValue = 1;
        else if (billingFrequency === 'monthly' || billingFrequency === 'month') billingCyclesValue = 12;
        else if (billingFrequency === 'quarterly') billingCyclesValue = 4;
        else if (billingFrequency === 'semiannually') billingCyclesValue = 2;
        else billingCyclesValue = 1; // Default 1 cycle
      }
      billingCyclesValue = Math.max(1, Math.round(billingCyclesValue)); // Ensure at least 1 cycle

      // Calculate end date based on calculated cycles and frequency
      if (billingFrequency === 'monthly' || billingFrequency === 'month') {
        end.setMonth(start.getMonth() + billingCyclesValue);
      } else if (billingFrequency === 'yearly' || billingFrequency === 'annually' || billingFrequency === 'year') {
        end.setFullYear(start.getFullYear() + billingCyclesValue);
      } else if (billingFrequency === 'quarterly') {
        end.setMonth(start.getMonth() + (billingCyclesValue * 3));
      } else if (billingFrequency === 'semiannually') {
        end.setMonth(start.getMonth() + (billingCyclesValue * 6));
      } else { // Includes 'one-time' or unknown - default to 1 month?
        end.setMonth(start.getMonth() + 1); // Default to 1 month term if frequency unclear or one-time
        // Already set billingCyclesValue to 1 above, no need to reassign
      }

      // Set day to last day of previous month
      end.setDate(0);

      termEndDate = end.toISOString().split('T')[0];

    } catch (e) {
      console.error("Error calculating end date for HubSpot item:", e);
      // Fallback: 1 year from start date
      const fallbackEnd = new Date(startDate);
      fallbackEnd.setFullYear(fallbackEnd.getFullYear() + 1);
      fallbackEnd.setDate(0); // Last day of previous month
      termEndDate = fallbackEnd.toISOString().split('T')[0];
      billingCyclesValue = (matchedPlan.billing_frequency === 'yearly') ? 1 : 12;
    }

    const newSelectedPlan: SelectedPlan = {
      subscription_plan_id: matchedPlan.id,
      subscription_plan: matchedPlan,
      quantity: quantity,
      unit_price: unitPrice,
      current_term_start: startDate, // Use wizard start date
      current_term_end: termEndDate,
      billing_start_terms: 'immediately', // Default: start immediately
      auto_renew: undefined, // To be set during review
      billing_cycles: billingCyclesValue,
      billing_by: 'by-self', // Default: charged by us
      needsReview: true, // <<< Mark for review
    };

    console.log("Successfully converted HubSpot item to SelectedPlan:", newSelectedPlan);
    return newSelectedPlan;
  };

  // Adicionar funções para calcular as métricas financeiras
  const calculateOneTimeTotal = () => {
    return state.selectedPlans.reduce((total, selectedPlan) => {
      // Verificar se é um plano one-time
      if (selectedPlan.subscription_plan.billing_period_type === 'one-time' || isForceOneTime(selectedPlan)) {
        return total + (selectedPlan.quantity * selectedPlan.unit_price);
      }
      return total;
    }, 0);
  };

  const calculateMRR = () => {
    return state.selectedPlans.reduce((total, selectedPlan) => {
      // Forçar MRR para determinados planos, independente dos outros critérios
      if (isForceMRR(selectedPlan)) {
        return total + (selectedPlan.quantity * selectedPlan.unit_price);
      }

      // Verificar explicitamente se não é one-time ou pagamento único
      const isOneTime = selectedPlan.subscription_plan.billing_period_type === 'one-time' || isForceOneTime(selectedPlan);
      const isPaymentUnique = selectedPlan.subscription_plan.billing_frequency === 'unique';
      
      if (!isOneTime && !isPaymentUnique) {
        // Para planos mensais, adicionar diretamente
        if (selectedPlan.subscription_plan.billing_frequency === 'monthly' ||
            selectedPlan.subscription_plan.billing_period_type === 'month') {
          return total + (selectedPlan.quantity * selectedPlan.unit_price);
        }
        
        // Para planos anuais, dividir por 12
        if (selectedPlan.subscription_plan.billing_frequency === 'yearly' ||
            selectedPlan.subscription_plan.billing_period_type === 'year') {
          return total + (selectedPlan.quantity * selectedPlan.unit_price / 12);
        }

        // Para planos trimestrais, dividir por 3
        if (selectedPlan.subscription_plan.billing_frequency === 'quarterly') {
          return total + (selectedPlan.quantity * selectedPlan.unit_price / 3);
        }

        // Para planos semestrais, dividir por 6
        if (selectedPlan.subscription_plan.billing_frequency === 'semiannually') {
          return total + (selectedPlan.quantity * selectedPlan.unit_price / 6);
        }
      }
      return total;
    }, 0);
  };

  const calculateARR = () => {
    return calculateMRR() * 12;
  };

  const calculateACV = () => {
    // ACV é o valor anual recorrente do primeiro ano + pagamentos one-time
    return calculateARR() + calculateOneTimeTotal();
  };

  const calculateTCV = () => {
    // TCV = one-time + (MRR * períodos de cobrança)
    let oneTimeTotal = calculateOneTimeTotal();
    
    // Valor recorrente total
    let recurringTotal = 0;
    
    // Calcular o valor total de todos os planos recorrentes durante o período do contrato
    state.selectedPlans.forEach(selectedPlan => {
      // Ignorar planos one-time (já contabilizados no oneTimeTotal)
      if (selectedPlan.subscription_plan.billing_period_type !== 'one-time' && !isForceOneTime(selectedPlan)) {
        const cycles = selectedPlan.billing_cycles || 1;
        let totalPlanValue = 0;
        
        // Calcular o valor total para cada plano com base na frequência de cobrança
        if (selectedPlan.subscription_plan.billing_frequency === 'monthly' || 
            selectedPlan.subscription_plan.billing_period_type === 'month') {
          // Para planos mensais: preço * quantidade * número de meses
          totalPlanValue = selectedPlan.quantity * selectedPlan.unit_price * cycles;
        } else if (selectedPlan.subscription_plan.billing_frequency === 'yearly' || 
                    selectedPlan.subscription_plan.billing_period_type === 'year') {
          // Para planos anuais: preço * quantidade * número de anos
          totalPlanValue = selectedPlan.quantity * selectedPlan.unit_price * cycles;
        } else if (selectedPlan.subscription_plan.billing_frequency === 'quarterly') {
          // Para planos trimestrais: preço * quantidade * número de trimestres
          totalPlanValue = selectedPlan.quantity * selectedPlan.unit_price * cycles;
        } else if (selectedPlan.subscription_plan.billing_frequency === 'semiannually') {
          // Para planos semestrais: preço * quantidade * número de semestres
          totalPlanValue = selectedPlan.quantity * selectedPlan.unit_price * cycles;
        }
        
        recurringTotal += totalPlanValue;
      }
    });
    
    // TCV = One-Time + Total de pagamentos recorrentes durante o contrato
    return oneTimeTotal + recurringTotal;
  };

  // Funções auxiliares para identificação de planos especiais
  const isForceOneTime = (plan: any) => {
    if (!plan || !plan.subscription_plan || !plan.subscription_plan.name) return false;
    
    const planName = plan.subscription_plan.name.toLowerCase();
    return planName.includes('consultoria') || 
           planName.includes('implantação') || 
           planName.includes('microsoft 365') ||
           planName.includes('pagamento único');
  };

  const isForceMRR = (plan: any) => {
    if (!plan || !plan.subscription_plan || !plan.subscription_plan.name) return false;
    
    const planName = plan.subscription_plan.name.toLowerCase();
    return planName.includes('teams phone') || 
           planName.includes('pay-as-you-go');
  };

  // Otimizar cálculos financeiros com useMemo
  const financialMetrics = useMemo(() => {
    if (state.selectedPlans.length === 0) {
      return { oneTime: 0, mrr: 0, arr: 0, acv: 0, tcv: 0 };
    }
    
    const oneTime = calculateOneTimeTotal();
    const mrr = calculateMRR();
    const arr = calculateARR();
    const acv = calculateACV();
    const tcv = calculateTCV();
    
    return { oneTime, mrr, arr, acv, tcv };
  }, [state.selectedPlans]);

  // Atualiza a lista quando mudam os filtros, ordenação ou página
  useEffect(() => {
    // Este effect só deve executar quando o modal Add Plan estiver aberto
    if (isAddPlanDialogOpen) {
      console.log("Modal de planos aberto, atualizando lista:", {
        page: currentPage, search: planSearchTerm
      });
      
      // Usar um timeout para debounce, evitando múltiplas chamadas rápidas
      const timeoutId = setTimeout(() => {
        fetchPlansPage(currentPage);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isAddPlanDialogOpen, currentPage, sortField, sortDirection, planSearchTerm, advancedFilters]);

  // Ref para controlar auto-review duplicado
  const autoReviewProcessing = useRef(false);

  // Effect to automatically open review dialog for plans needing review
  useEffect(() => {
    // Start auto-review process when plans are imported and need review
    const plansNeedingReview = state.selectedPlans.filter(plan => plan.needsReview);
    
    // --- Add check: Only run if AddPlan dialog is NOT opening --- 
    if (isAddPlanDialogOpen || autoReviewProcessing.current) return;
    
    // If we just imported plans that need review and we're not already in auto-review mode
    if (plansNeedingReview.length > 0 && !autoReviewMode && !isEditPlanDialogOpen) {
      const firstIndex = state.selectedPlans.findIndex(plan => plan.needsReview);
      if (firstIndex >= 0) {
        console.log("Starting auto-review process for imported plans...");
        autoReviewProcessing.current = true;
        setAutoReviewMode(true);
        setCurrentReviewIndex(firstIndex);
        
        // Open edit dialog for the first plan needing review
        setTimeout(() => {
          // --- Add check: Ensure AddPlan dialog didn't open in the meantime --- 
          if (!isAddPlanDialogOpen) {
            handleEditPlan(firstIndex);
          }
          autoReviewProcessing.current = false;
        }, 200); // Timeout might still be needed for state updates
      }
    }
  }, [state.selectedPlans, autoReviewMode, isEditPlanDialogOpen, isAddPlanDialogOpen]); // Add isAddPlanDialogOpen to dependencies



  // Debounced effect to calculate end date automatically
  useEffect(() => {
    const timeoutId = setTimeout(() => {
    // Adicionado guarda para só rodar se houver um plano selecionado
    if (!selectedPlan) {
        setCurrentTermEnd('');
        return;
    }

    if (currentTermStart && billingCycles && selectedPlan.billing_frequency) {
      try {
        const startDate = parseISO(currentTermStart); // Use parseISO for better format handling
        if (!isValid(startDate)) {
          console.error("Invalid start date format:", currentTermStart);
          setCurrentTermEnd(''); // Clear end date if start date is invalid
          return;
        }

        let endDate: Date;
        const billingFrequency = getEffectiveFrequency(selectedPlan) as string;
        const cycles = typeof billingCycles === 'number' ? billingCycles : parseInt(billingCycles as unknown as string, 10) || 1;
        
        if (billingFrequency === 'monthly' || billingFrequency === 'month') {
          endDate = addMonths(startDate, cycles);
        } else if (billingFrequency === 'yearly' || billingFrequency === 'annually' || billingFrequency === 'year') {
          endDate = addYears(startDate, cycles);
        } else if (billingFrequency === 'quarterly') {
          endDate = addMonths(startDate, cycles * 3);
        } else if (billingFrequency === 'semiannually') {
          endDate = addMonths(startDate, cycles * 6);
        } else if (billingFrequency === 'one-time' || billingFrequency === 'unique') {
          // Para planos one-time, assumimos uma duração de 1 mês por padrão
          endDate = addMonths(startDate, 1);
        } else {
          console.warn("Frequência de cobrança não suportada para cálculo de data de término:", billingFrequency);
          setCurrentTermEnd(''); // Limpar data de término para frequências não suportadas
          return;
        }

        // Subtrair um dia para obter o último dia do período
        // Apenas subtrair se não for um plano one-time
        if (billingFrequency !== 'one-time') {
          endDate = subDays(endDate, 1);
        }

        console.log("Calculando data de término:", {
          start: currentTermStart,
          freq: billingFrequency,
          cycles: cycles,
          calculatedEnd: format(endDate, 'yyyy-MM-dd')
        });

        setCurrentTermEnd(format(endDate, 'yyyy-MM-dd'));
      } catch (error) {
        console.error("Erro calculando data de término:", error);
        setCurrentTermEnd(''); // Limpar data de término em caso de erro
      }
    } else {
      // Dados insuficientes para cálculo
      debugLog("Dados insuficientes para cálculo de data de término:", { 
          currentTermStart, 
          billingCycles, 
          billingFrequency: selectedPlan?.billing_frequency 
      });
      setCurrentTermEnd(''); // Limpar data de término se dados estiverem faltando
    }
    }, 150); // Debounce de 150ms

    return () => clearTimeout(timeoutId);
  }, [currentTermStart, billingCycles, selectedPlan]); // Depender do selectedPlan completo

  // Função utilitária para calcular a data de término com base na data de início, número de ciclos e tipo de plano
  const calculateEndDate = (startDateStr: string, cycles: number, planFrequency?: string): string => {
    if (!startDateStr || !cycles || !planFrequency) return '';
    
    try {
      const startDate = parseISO(startDateStr);
      if (!isValid(startDate)) return ''; // Data inválida
      
      const freq = planFrequency.toLowerCase();
      let endDate: Date;
      
      // Aplicar a lógica de cálculo baseada na frequência do plano usando date-fns
      if (freq === 'monthly' || freq === 'month') {
        endDate = addMonths(startDate, cycles);
      } else if (freq === 'yearly' || freq === 'annually' || freq === 'year') {
        endDate = addYears(startDate, cycles);
      } else if (freq === 'quarterly') {
        endDate = addMonths(startDate, cycles * 3);
      } else if (freq === 'semiannually') {
        endDate = addMonths(startDate, cycles * 6);
      } else if (freq === 'one-time' || freq === 'unique') {
        // Planos one-time assumem 1 mês por padrão
        endDate = addMonths(startDate, 1);
      } else {
        return ''; // Frequência desconhecida
      }
      
      // Subtrair um dia para obter o último dia do período (exceto para one-time)
      if (freq !== 'one-time' && freq !== 'unique') {
        endDate = subDays(endDate, 1);
      }
      
      // Formatar a data como YYYY-MM-DD
      return format(endDate, 'yyyy-MM-dd');
    } catch (error) {
      console.error('Erro ao calcular data de término:', error);
      return '';
    }
  };

  // REMOVIDO: Efeito duplicado de cálculo de data
  // O cálculo já é feito pelo useEffect principal com debouncing
  /*
  useEffect(() => {
    // Só executar quando o modal de edição estiver aberto
    if (isEditPlanDialogOpen && currentTermStart && billingCycles && selectedPlan) {
      const effectiveFreq = getEffectiveFrequency(selectedPlan);
      if (effectiveFreq) {
        const endDate = calculateEndDate(
          currentTermStart,
          typeof billingCycles === 'number' ? billingCycles : parseInt(billingCycles as any, 10) || 1,
          effectiveFreq
        );
        
        console.log("Atualizando data de término no modal de edição:", {
          start: currentTermStart,
          cycles: billingCycles,
          freq: effectiveFreq,
          calculatedEnd: endDate
        });
        
        setCurrentTermEnd(endDate);
      }
    }
  }, [isEditPlanDialogOpen, currentTermStart, billingCycles, selectedPlan]);
  */

  return (
    <SubscriptionWizardLayout
      currentStep={5}
      totalSteps={6}
      title="Nova Assinatura - Planos"
      description="Selecione os planos para esta assinatura"
      onNext={handleSubmit}
      onPrevious={() => navigate('/subscription-wizard/step4')}
    >
      <div className="space-y-6">
        {/* REMOVE HubSpot opportunity items banner */}
        {/* 
        {hubspotLineItems.length > 0 && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-blue-800 font-extrabold text-sm flex items-center">
                    <Download className="h-4 w-4 mr-2" />
                    Itens da oportunidade HubSpot encontrados
                  </h3>
                  <p className="text-blue-600 text-xs mt-1">
                    Encontramos {hubspotLineItems.length} itens na oportunidade {state.selectedOpportunity?.dealname}
                  </p>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  onClick={() => setShowHubspotItemsDialog(true)} // REMOVE THIS onClick or the entire Button/Card
                >
                  Importar itens da oportunidade
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        */}



        {/* Métricas Financeiras - Movidas para o topo */}
        <div className="bg-muted p-4 rounded-md">
          <div className="text-lg font-extrabold mb-3">Métricas Financeiras</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* One-Time Card */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="text-sm font-extrabold text-muted-foreground">One-Time</div>
                <div className="bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded-full">Único</div>
              </div>
              <div className="mt-2 text-2xl font-extrabold">
                {formatCurrency(financialMetrics.oneTime, state.currencyCode)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                One-Time Payments
              </div>
            </div>
            
            {/* MRR Card */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="text-sm font-extrabold text-muted-foreground">MRR</div>
                <div className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs px-2 py-1 rounded-full">Mensal</div>
              </div>
              <div className="mt-2 text-2xl font-extrabold">
                {formatCurrency(financialMetrics.mrr, state.currencyCode)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Monthly Recurring Revenue
              </div>
            </div>

            {/* ARR Card */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="text-sm font-extrabold text-muted-foreground">ARR</div>
                <div className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs px-2 py-1 rounded-full">Anual</div>
              </div>
              <div className="mt-2 text-2xl font-extrabold">
                {formatCurrency(financialMetrics.arr, state.currencyCode)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Annual Recurring Revenue
              </div>
            </div>

            {/* ACV Card */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="text-sm font-extrabold text-muted-foreground">ACV</div>
                <div className="bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-xs px-2 py-1 rounded-full">Primeiro Ano</div>
              </div>
              <div className="mt-2 text-2xl font-extrabold">
                {formatCurrency(financialMetrics.acv, state.currencyCode)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Annual Contract Value
              </div>
            </div>

            {/* TCV Card */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="text-sm font-extrabold text-muted-foreground">TCV</div>
                <div className="bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 text-xs px-2 py-1 rounded-full">Duração Total</div>
              </div>
              <div className="mt-2 text-2xl font-extrabold">
                {formatCurrency(calculateTCV(), state.currencyCode)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Total Contract Value
              </div>
            </div>
          </div>
        </div>

        {/* Tabela de planos selecionados */}
        <div className="border rounded-lg overflow-hidden shadow-sm">
          <div className="flex items-center justify-between bg-muted p-4">
            <h3 className="text-lg font-extrabold flex items-center gap-2">
              Planos Selecionados
              {state.selectedPlans.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {state.selectedPlans.length}
                </Badge>
              )}
            </h3>
          <Button 
            onClick={() => setIsAddPlanDialogOpen(true)}
              size="sm"
              className="gap-1"
            disabled={loading}
          >
              <Plus className="h-4 w-4" />
            Adicionar Plano
          </Button>
        </div>

            {state.selectedPlans.length === 0 ? (
            <div className="p-8 text-center border-t">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-4">
                <Package className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-extrabold">Nenhum plano selecionado</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-4">
                Selecione os planos que farão parte desta assinatura.
              </p>
              <Button
                onClick={() => setIsAddPlanDialogOpen(true)}
                variant="outline"
                className="gap-1"
              >
                <Plus className="h-4 w-4" />
                Adicionar Plano
              </Button>
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left py-3 px-4 font-extrabold text-sm text-muted-foreground">Plano</th>
                    <th className="text-center py-3 px-4 font-extrabold text-sm text-muted-foreground">Quantidade</th>
                    <th className="text-right py-3 px-4 font-extrabold text-sm text-muted-foreground">Preço Unitário</th>
                    <th className="text-center py-3 px-4 font-extrabold text-sm text-muted-foreground">Fidelidade</th>
                    <th className="text-left py-3 px-4 font-extrabold text-sm text-muted-foreground">Vigência</th>
                    <th className="text-right py-3 px-4 font-extrabold text-sm text-muted-foreground">Total</th>
                    <th className="text-center py-3 px-4 font-extrabold text-sm text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {state.selectedPlans.map((selectedPlan, index) => (
                    <tr key={index} className="hover:bg-muted/30 border-b last:border-b-0">
                      <td className="p-4">
                        <div className="flex flex-col">
                          <div className="font-extrabold">{selectedPlan.subscription_plan.name}</div>
                          {selectedPlan.subscription_plan.sku && (
                            <span className="text-xs text-muted-foreground font-mono">
                              SKU: {selectedPlan.subscription_plan.sku}
                            </span>
                          )}
                            {selectedPlan.subscription_plan.description && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-5 w-5 mt-0.5 -ml-0.5 rounded-full">
                                      <Info className="h-3 w-3" />
                                  </Button>
                                  </TooltipTrigger>
                                <TooltipContent side="right">{selectedPlan.subscription_plan.description}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-extrabold">{selectedPlan.quantity}</span>
                      </td>
                      <td className="p-4 text-right font-extrabold">
                        {formatCurrency(selectedPlan.unit_price, state.currencyCode)}
                        <div className="text-xs text-muted-foreground">
                          {selectedPlan.subscription_plan.billing_frequency && 
                            `/${translateBillingFrequency(selectedPlan.subscription_plan.billing_frequency)}`}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <Badge variant="outline">
                          {formatCommitmentTerm(
                            selectedPlan.subscription_plan.billing_period_type,
                            selectedPlan.subscription_plan.billing_period_duration,
                            selectedPlan.subscription_plan.billing_frequency
                          )}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs py-0.5 px-1.5 bg-muted rounded text-muted-foreground">Período</span>
                            <span className="text-sm">
                              {formatDate(selectedPlan.current_term_start)} a {formatDate(selectedPlan.current_term_end)}
                            </span>
                        </div>
                          <div className="flex flex-col gap-1 mt-1">
                            <div className="flex items-center gap-2">
                              <Badge variant={selectedPlan.auto_renew ? "default" : "secondary"} className="text-xs">
                                {selectedPlan.auto_renew ? 'Renovação automática' : 'Período fixo'}
                              </Badge>
                        </div>
                            <div className="flex gap-2 text-xs text-muted-foreground">
                              <span>Ciclos: {selectedPlan.billing_cycles}</span>
                              <span>•</span>
                              <span>Cobrança: {selectedPlan.billing_by === 'by-self' ? 'Própria' : 'Distribuidor'}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-extrabold">
                          {formatCurrency(selectedPlan.quantity * selectedPlan.unit_price, state.currencyCode)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end space-x-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => handleEditPlan(index)} className={selectedPlan.needsReview ? 'ring-2 ring-destructive' : ''}>
                            <Edit className="h-4 w-4" />
                          </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Editar Detalhes do Plano</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => removeSelectedPlan(index)} className="text-destructive hover:text-destructive">
                                  <Trash2 className="h-4 w-4" />
                          </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Remover Plano</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={isAddPlanDialogOpen} onOpenChange={setIsAddPlanDialogOpen}>
          <DialogContent className="sm:max-w-[850px] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Adicionar Plano</DialogTitle>
              <DialogDescription>
                Selecione um plano e configure os detalhes da assinatura.
              </DialogDescription>
            </DialogHeader>
            
            {!selectedPlanId ? (
              // Lista de seleção de planos
              <>
                <div className="flex items-center gap-2 mb-4">
                  <div className="relative flex-1">
                    <Input
                      placeholder="Pesquisar plano por nome ou SKU"
                      value={planSearchTerm}
                      onChange={(e) => {
                        setPlanSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="pr-10"
                    />
                    {planSearchTerm ? (
                      <button 
                        type="button"
                        onClick={() => {
                          setPlanSearchTerm('');
                          setCurrentPage(1);
                        }}
                        className="absolute right-10 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground"
                        title="Limpar pesquisa"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  </div>
                  
                  <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="gap-2"
                        aria-label="Filtros avançados"
                      >
                        <SlidersHorizontal className="h-4 w-4" />
                        <span className="hidden sm:inline">Filtros</span>
                        {hasActiveFilters && (
                          <Badge variant="secondary" className="ml-1 h-5 px-1">
                            {advancedFilters.familyIds.length + 
                             advancedFilters.billingFrequencies.length}
                          </Badge>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-extrabold">Filtros Avançados</h4>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={clearAllFilters}
                            className="h-8 px-2 text-xs"
                          >
                            Limpar Filtros
                          </Button>
                        </div>
                        
                        <Separator />
                        
                        <div className="space-y-2">
                          <h5 className="text-sm font-extrabold">Frequência de Cobrança</h5>
                          <ScrollArea className="h-24">
                            <div className="space-y-2">
                              {['monthly', 'yearly', 'quarterly', 'semiannually'].map((freq) => (
                                <div key={freq} className="flex items-center space-x-2">
                                  <Checkbox 
                                    id={`freq-${freq}`} 
                                    checked={advancedFilters.billingFrequencies.includes(freq)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        updateAdvancedFilters({
                                          billingFrequencies: [...advancedFilters.billingFrequencies, freq]
                                        });
                                      } else {
                                        updateAdvancedFilters({
                                          billingFrequencies: advancedFilters.billingFrequencies.filter(f => f !== freq)
                                        });
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`freq-${freq}`} className="text-sm">
                                    {translateBillingFrequency(freq)}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="flex-1 overflow-hidden flex flex-col">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="ml-2">Carregando planos...</span>
                    </div>
                  ) : plans.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Nenhum plano encontrado com os filtros atuais
                    </div>
                  ) : (
                    <div className="flex-1 overflow-hidden flex flex-col">
                      {/* Tabela com scroll próprio - garantindo altura fixa e scroll */}
                      <div className="border rounded-md overflow-hidden" style={{ height: '400px', overflowY: 'auto' }}>
                        <Table>
                          <TableHeader className="sticky top-0 bg-background z-10">
                            <TableRow>
                              <TableHead className="w-[40px]"></TableHead>
                              <TableHead>
                                <Button 
                                  variant="ghost" 
                                  className="p-0 h-auto font-extrabold flex items-center gap-1"
                                  onClick={() => toggleSort('name')}
                                >
                                  Plano
                                  <ArrowUpDown className="h-3 w-3" />
                                </Button>
                              </TableHead>
                              <TableHead>
                                <Button 
                                  variant="ghost" 
                                  className="p-0 h-auto font-extrabold flex items-center gap-1"
                                  onClick={() => toggleSort('price')}
                                >
                                  Preço
                                  <ArrowUpDown className="h-3 w-3" />
                                </Button>
                              </TableHead>
                              <TableHead className="w-[100px] text-center">Fidelidade</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {plans.map((plan) => {
                              const isSelected = selectedPlanId === plan.id;
                              return (
                                <TableRow 
                                  key={plan.id} 
                                  className={`cursor-pointer ${isSelected ? 'bg-primary/5' : ''}`}
                                  onClick={() => handleSelectPlan(plan)}
                                >
                                  <TableCell className="p-2">
                                    <div className="flex justify-center">
                                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex flex-col">
                                      <div className="font-extrabold">{plan.name}</div>
                                      {plan.sku && (
                                        <span className="text-xs text-muted-foreground font-mono">
                                          SKU: {plan.sku}
                                        </span>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="font-extrabold">
                                    {formatPriceWithFrequency(plan)}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Badge variant="outline">
                                      {formatCommitmentTerm(
                                        plan.billing_period_type,
                                        plan.billing_period_duration,
                                        plan.billing_frequency
                                      )}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {/* Paginação */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t p-4">
                          <div className="text-sm text-muted-foreground">
                            Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1} a {Math.min(currentPage * ITEMS_PER_PAGE, totalPlans)} de {totalPlans} planos
                        </div>
                          <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                              size="icon"
                              className="h-8 w-8"
                            onClick={() => goToPage(1)}
                              disabled={currentPage === 1}
                          >
                              <ChevronsLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                              size="icon"
                              className="h-8 w-8"
                            onClick={() => goToPage(currentPage - 1)}
                              disabled={currentPage === 1}
                          >
                              <ChevronLeft className="h-4 w-4" />
                          </Button>
                          
                            <span className="text-sm font-extrabold px-2">
                            {currentPage} / {totalPages}
                          </span>
                          
                          <Button
                            variant="outline"
                              size="icon"
                              className="h-8 w-8"
                            onClick={() => goToPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                          >
                              <ChevronRight className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                              size="icon"
                              className="h-8 w-8"
                            onClick={() => goToPage(totalPages)}
                              disabled={currentPage === totalPages}
                          >
                              <ChevronsRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Formulário de configuração de plano
              <div className="space-y-6">
                <div className="bg-muted/30 p-4 rounded-md">
                  <div className="text-sm font-extrabold mb-2">Plano Selecionado:</div>
                  <div className="text-lg font-extrabold">{selectedPlan?.name}</div>
                    {selectedPlan?.sku && (
                    <div className="text-xs text-muted-foreground font-mono mt-1">
                      SKU: {selectedPlan.sku}
                  </div>
                  )}
                  {selectedPlan?.description && (
                    <div className="text-sm text-muted-foreground mt-2">
                      {selectedPlan.description}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="quantity">Quantidade</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      disabled={autoReviewMode}
                      className={autoReviewMode ? "bg-muted cursor-not-allowed" : ""}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="unitPrice">Preço Unitário ({state.currencyCode})</Label>
                    <Input
                      id="unitPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(e.target.value)}
                      disabled={autoReviewMode}
                      className={autoReviewMode ? "bg-muted cursor-not-allowed" : ""}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="billingCycles">Ciclos de Cobrança</Label>
                    <Input
                        id="billingCycles"
                      type="number"
                      min="1"
                        value={billingCycles}
                        onChange={(e) => {
                        const newCycles = parseInt(e.target.value, 10);
                        setBillingCycles(newCycles);
                        
                        // Calcular data de término imediatamente quando os ciclos mudam
                        if (selectedPlan?.billing_frequency && currentTermStart) {
                          const endDate = calculateEndDate(
                            currentTermStart, 
                            typeof billingCycles === 'number' ? billingCycles : 1, 
                            selectedPlan.billing_frequency
                          );
                          setCurrentTermEnd(endDate);
                        }
                      }}
                      disabled={selectedPlan?.billing_frequency?.toLowerCase() === 'one-time' || autoReviewMode}
                      className={autoReviewMode ? "bg-muted cursor-not-allowed" : ""}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedPlan?.billing_frequency === 'monthly' ? 'Quantos meses?' : 
                      selectedPlan?.billing_frequency === 'yearly' ? 'Quantos anos?' : 
                      selectedPlan?.billing_frequency === 'quarterly' ? 'Quantos trimestres?' : 
                      selectedPlan?.billing_frequency === 'semiannually' ? 'Quantos semestres?' : 
                      'Número de ciclos de cobrança'}
                    </p>
                </div>

                  <div>
                    <Label htmlFor="billingStartTerms">Início da Cobrança</Label>
                    <Select 
                        value={billingStartTerms} 
                      onValueChange={(value) => setBillingStartTerms(value as any)}
                    >
                        <SelectTrigger id="billingStartTerms">
                        <SelectValue placeholder="Selecione uma opção" />
                      </SelectTrigger>
                      <SelectContent>
                          <SelectItem value="immediately">Imediatamente</SelectItem>
                        <SelectItem value="specific-date">Data Específica</SelectItem>
                      </SelectContent>
                    </Select>
                </div>

                  <div>
                    <Label htmlFor="current_term_start">Data de Início</Label>
                    <Input
                      id="current_term_start"
                      type="date"
                      value={currentTermStart}
                      onChange={(e) => {
                        // Atualizar a data de início
                        setCurrentTermStart(e.target.value);
                        
                        // Lógica para definir automaticamente o início da cobrança
                        const selectedDate = new Date(e.target.value);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
                        
                        if (selectedDate.getTime() === today.getTime()) {
                          setBillingStartTerms('immediately');
                        } else {
                          setBillingStartTerms('specific-date');
                        }
                        
                        // Recalcular data de término se tivermos as informações necessárias
                        if (selectedPlan?.billing_frequency) {
                          console.log("Data de início alterada, recalculando data de término");
                        }
                      }}
                      className="h-8"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="current_term_end">Data de Término (Calculada)</Label>
                    <Input
                      id="current_term_end"
                      type="date"
                      value={currentTermEnd}
                      disabled
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Calculada automaticamente com base no plano e ciclos
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="autoRenew">Renovação Automática</Label>
                    <Switch
                      id="autoRenew"
                      checked={autoRenew}
                      onCheckedChange={setAutoRenew}
                    />
                </div>
                  
                  <div>
                    <Label htmlFor="billingBy">Cobrado Por</Label>
                    <Select 
                      value={billingBy} 
                      onValueChange={(value) => setBillingBy(value as any)}
                    >
                      <SelectTrigger id="billingBy">
                        <SelectValue placeholder="Selecione uma opção" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="by-self">Nós Mesmos</SelectItem>
                        <SelectItem value="by-reseller">Revendedor</SelectItem>
                        <SelectItem value="by-manufacturer">Fabricante</SelectItem>
                      </SelectContent>
                    </Select>
              </div>
                </div>

                  <div className="bg-muted/30 border rounded-md p-3 flex flex-col justify-between">
                    <div className="text-sm font-extrabold mb-1">Total</div>
                    <div className="text-xl font-extrabold text-right">
                      {formatCurrency(parseInt(quantity || "0") * parseFloat(unitPrice || "0"), state.currencyCode)}
                    </div>
                    <div className="text-xs text-muted-foreground text-right mt-1">
                      Valor para {billingCycles || 1} {billingCycles === 1 ? 'ciclo' : 'ciclos'}
            </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              {!selectedPlanId ? (
              <Button variant="outline" onClick={() => setIsAddPlanDialogOpen(false)}>
                Cancelar
              </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setSelectedPlanId('')}>
                    Voltar para Lista
                  </Button>
              <Button 
                onClick={handleAddPlan} 
                    disabled={isAddingPlan || !currentTermStart || !currentTermEnd}
              >
                {isAddingPlan ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Adicionar Plano'
                )}
              </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para editar plano */}
        <Dialog 
          open={isEditPlanDialogOpen} 
          onOpenChange={(open) => {
            // In auto-review mode, prevent closing the dialog manually
            if (autoReviewMode && !open) {
              toast.warning("Por favor, revise e salve os detalhes do plano antes de continuar.");
              return;
            }
            setIsEditPlanDialogOpen(open);
          }}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader className="pb-2">
              <DialogTitle>
                {autoReviewMode ? "Revisão Obrigatória de Plano" : "Editar Plano"}
                {autoReviewMode && currentReviewIndex !== null && (
                  <span className="ml-2 text-sm text-muted-foreground">
                    ({currentReviewIndex + 1}/{state.selectedPlans.filter(p => p.needsReview || p.subscription_plan_id === state.selectedPlans[currentReviewIndex]?.subscription_plan_id).length})
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                {autoReviewMode 
                  ? "Revise todos os detalhes do plano importado. Todos os campos precisam ser verificados antes de continuar."
                  : "Edite os detalhes do plano selecionado."}
              </DialogDescription>
            </DialogHeader>
            
            <ScrollArea className="pr-4 max-h-[60vh]">
              {/* Plan info - full width */}
              <div className="bg-muted/30 p-3 rounded-md mb-4">
                <div className="text-sm font-extrabold mb-1">Informações do Plano</div>
                <div className="text-lg font-extrabold">{selectedPlan?.name}</div>
                <div className="text-sm text-muted-foreground">
                  {selectedPlan?.billing_frequency ? translateBillingFrequency(selectedPlan.billing_frequency) : 'N/A'}
                  </div>
                </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Quantidade */}
                <div>
                  <Label htmlFor="quantity" className="text-sm">Quantidade</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      disabled={autoReviewMode}
                      className={`h-8 ${autoReviewMode ? "bg-muted cursor-not-allowed" : ""}`}
                    />
                  </div>

                {/* Preço */}
                <div>
                  <Label htmlFor="unitPrice" className="text-sm">Preço Unitário ({state.currencyCode})</Label>
                    <Input
                      id="unitPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(e.target.value)}
                      disabled={autoReviewMode}
                      className={`h-8 ${autoReviewMode ? "bg-muted cursor-not-allowed" : ""}`}
                    />
                  </div>

                {/* Ciclos */}
                  <div>
                  <Label htmlFor="billingCycles" className="text-sm">Ciclos de Cobrança</Label>
                    <Input
                      id="billingCycles"
                      type="number"
                    min="1"
                      value={billingCycles}
                      onChange={(e) => {
                      const newCycles = parseInt(e.target.value, 10);
                      setBillingCycles(newCycles);
                      
                      // Calcular data de término imediatamente quando os ciclos mudam
                      if (selectedPlan?.billing_frequency && currentTermStart) {
                        const endDate = calculateEndDate(
                          currentTermStart, 
                          typeof billingCycles === 'number' ? billingCycles : 1, 
                          selectedPlan.billing_frequency
                        );
                        setCurrentTermEnd(endDate);
                      }
                    }}
                    disabled={isBillingCyclesDisabled || autoReviewMode} // Apply the disabled state
                    className={`h-8 ${autoReviewMode ? "bg-muted cursor-not-allowed" : ""}`}
                    />
                  </div>

                                 {/* Início da Cobrança */}
                 <div>
                   <Label htmlFor="billingStartTerms" className="text-sm">Início da Cobrança</Label>
                   <Select 
                     value={billingStartTerms}
                     onValueChange={(value) => setBillingStartTerms(value as any)}
                   >
                     <SelectTrigger className="h-8">
                       <SelectValue placeholder="Selecione..." />
                     </SelectTrigger>
                     <SelectContent>
                       <SelectItem value="immediately">Imediatamente</SelectItem>
                       <SelectItem value="specific-date">Data Específica</SelectItem>
                     </SelectContent>
                   </Select>
                 </div>

                 {/* Datas de Início e Término - colocadas lado a lado */}
                 <div className="lg:col-span-2 grid grid-cols-2 gap-3">
                   {/* Data de início */}
                   <div>
                     <Label htmlFor="currentTermStart" className="text-sm">Data de Início</Label>
                     <Input
                       id="currentTermStart"
                       type="date"
                       value={currentTermStart}
                       onChange={(e) => {
                         // Atualizar a data de início
                         setCurrentTermStart(e.target.value);
                         
                         // Lógica para definir automaticamente o início da cobrança
                         const selectedDate = new Date(e.target.value);
                         const today = new Date();
                         today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
                         
                         if (selectedDate.getTime() === today.getTime()) {
                           setBillingStartTerms('immediately');
                         } else {
                           setBillingStartTerms('specific-date');
                         }
                         
                         // Recalcular data de término se tivermos as informações necessárias
                         if (selectedPlan?.billing_frequency) {
                           console.log("Data de início alterada, recalculando data de término");
                         }
                       }}
                       className="h-8"
                     />
                     </div>

                   {/* Data de Término */}
                   <div>
                     <Label htmlFor="currentTermEnd" className="text-sm">Data de Término (Calculada)</Label>
                     <Input
                       id="currentTermEnd"
                       type="date"
                       value={currentTermEnd}
                       disabled
                       className="h-8"
                     />
                 </div>
               </div>

                {/* Cobrado por */}
                <div>
                  <Label htmlFor="billingBy" className="text-sm">Cobrado por</Label>
                    <Select 
                    value={billingBy}
                    onValueChange={(value) => setBillingBy(value as any)}
                    >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                      <SelectItem value="by-self">Nós Mesmos</SelectItem>
                      <SelectItem value="by-reseller">Revendedor</SelectItem>
                      <SelectItem value="by-manufacturer">Fabricante</SelectItem>
                      </SelectContent>
                    </Select>
                </div>

                {/* Renovação Automática */}
                  <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="autoRenew" className="text-sm">Renovação Automática</Label>
                    <Switch 
                      id="autoRenew"
                      checked={autoRenew}
                      onCheckedChange={setAutoRenew}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    O plano será renovado automaticamente
                  </div>
                </div>

                {/* Valor Total */}
                <div className="bg-muted/30 p-3 rounded-md">
                  <div className="text-sm font-extrabold mb-1">Valor Total</div>
                  <div className="text-lg font-extrabold">
                    {formatCurrency(parseInt(quantity || "0") * parseFloat(unitPrice || "0"), state.currencyCode)}
                </div>
                  <div className="text-xs text-muted-foreground">
                    Total para {billingCycles || 1} {billingCycles === 1 ? 'ciclo' : 'ciclos'}
              </div>
            </div>
              </div>
            </ScrollArea>
            
            <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between items-center pt-4 mt-2">
              {autoReviewMode && (
                <div className="text-sm text-muted-foreground mb-3 sm:mb-0">
                  <div className="flex items-center">
                    <Info className="h-4 w-4 mr-1 text-blue-500" />
                    Revisão obrigatória de todos os planos importados
            </div>
              </div>
              )}
              <div className="space-x-2">
                {/* Only show Cancel button if not in auto-review mode */}
                {!autoReviewMode && (
                  <Button variant="outline" onClick={() => setIsEditPlanDialogOpen(false)}>
                Cancelar
              </Button>
                )}
                <Button onClick={handleUpdatePlan} disabled={isUpdatingPlan}>
                  {isUpdatingPlan ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {autoReviewMode ? "Salvar e Avançar" : "Salvar"}
              </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SubscriptionWizardLayout>
  );
};

export default Step5Plans; 